import { createRoute } from '@tanstack/react-router';
import { Authenticate, layoutRoute } from '@/router/root-routes.tsx';
import { AppAssistanceButton } from '@/components/app/app-assistance-button.tsx';
import { useAuth } from '@/stores/auth.store.ts';
import { usePageProps } from '@/hooks/use-page-props.ts';
import { useGetMemosQuery } from '@/app/account/api/queries.ts';
import { usePagination } from '@/components/table/use-pagination.ts';
import { useMemo } from 'react';
import { useAppTable } from '@/components/table/use-app-table.ts';
import { AppTable, ExpandRowCell } from '@/components/table/app-table.tsx';
import { TMemo } from '@/types/models.ts';
import { SampleMemoExpanded } from '@/app/account/samples/sample-memo-expanded.tsx';
import { ColumnDef } from '@tanstack/react-table';

export const samplesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'account/samples',
  component: SamplesPage,
  beforeLoad: Authenticate(),
});

function getMemoExpanded(memo: TMemo) {
  return <SampleMemoExpanded memo={memo} />;
}

const chunkSize = 3;
export function SampleItemsCell({ memo }: { memo: TMemo }) {
  const chunk = [];
  for (let i = 0; i < chunkSize && i < memo.items.length; i++) {
    chunk.push(memo.items[i]);
  }
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {chunk.map((item) => (
        <div key={item.id} title={item.name}>
          <img className="w-12 h-12 object-cover" src={item.img_url} alt={item.name} />
        </div>
      ))}
      {memo.items.length > chunkSize && (
        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 border">
          +{memo.items.length - chunkSize}
        </div>
      )}
    </div>
  );
}

function SampleColumns(): ColumnDef<TMemo>[] {
  return [
    { header: 'Project Name', accessorFn: (row) => row.project.name, meta: { cellClassName: 'text-xl' } },
    { header: 'Ship Date', accessorFn: (row) => row.shipped_date, size: 200 },
    { header: 'Due Date', accessorFn: (row) => row.due_date, size: 200 },
    { header: 'Items', cell: ({ row }) => <SampleItemsCell memo={row.original} />, meta: { className: 'text-center' } },
    { header: 'Status', accessorKey: 'status', size: 150 },
    {
      header: '',
      id: 'expand',
      size: 50,
      meta: { align: 'center' },
      cell: ({ row }) => (
        <div className="text-center">
          <ExpandRowCell row={row} />
        </div>
      ),
    },
  ];
}

function SamplesPage() {
  usePageProps({
    title: 'Samples',
  });
  const { user } = useAuth();
  const pagination = usePagination();
  const query = useGetMemosQuery({ ...pagination.params });
  const columns = useMemo(() => SampleColumns(), []);
  const at = useAppTable({
    query,
    columns,
    pagination,
  });
  return (
    <div className="max-w-8xl mx-auto py-4">
      <div className="grid lg:grid-cols-3 mb-4">
        <div className="p-1"></div>
        <div className="text-center">
          <h1>SAMPLES</h1>
        </div>
        <div className="flex lg:justify-end">
          <AppAssistanceButton
            title="New Sample"
            message="I am looking for help requesting new samples."
            subject={`New sample assistance request from ${user.full_name}`}
          />
        </div>
      </div>
      <div>
        <AppTable at={at} getExpanded={getMemoExpanded} />
      </div>
    </div>
  );
}
